import { useState } from "react";
import { NavLink, Outlet, useNavigate, Navigate } from "react-router";
import {
  LayoutDashboard,
  User,
  FileText,
  MessageCircle,
  Gift,
  FolderOpen,
  Compass,
  Settings,
  LogOut,
  Zap,
  Bell,
  ChevronRight,
  X,
  Menu,
  Sparkles,
  BrainCircuit,
  GraduationCap,
  Star,
  UsersRound,
} from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { useAuth } from "../../context/AuthContext";
import logo from "../../assets/logo/logo.jpeg";

const NAV_ITEMS = [
  {
    to: "/dashboard",
    icon: <LayoutDashboard className="w-4 h-4" />,
    label: "Overview",
    exact: true,
  },
  {
    to: "/dashboard/profile",
    icon: <User className="w-4 h-4" />,
    label: "My Profile",
  },
  {
    to: "/dashboard/exam-hub",
    icon: <GraduationCap className="w-4 h-4" />,
    label: "Exam Hub",
    labelSuffix: <Star className="w-3 h-3 fill-amber-400 text-amber-400" />,
  },
  {
    to: "/dashboard/course-finder",
    icon: <Sparkles className="w-4 h-4" />,
    label: "Course Finder",
  },
  {
    to: "/dashboard/career-test-result",
    icon: <BrainCircuit className="w-4 h-4" />,
    label: "Career Test",
    badgeColor: "bg-emerald-500",
  },
  {
    to: "/dashboard/referral",
    icon: <UsersRound className="w-4 h-4" />,
    label: "Referrals",
  },
  // {
  //   to: "/dashboard/applications",
  //   icon: <FileText className="w-4 h-4" />,
  //   label: "Applications",
  //   badge: "4",
  // },
  // {
  //   to: "/dashboard/chat",
  //   icon: <MessageCircle className="w-4 h-4" />,
  //   label: "My Counsellor",
  //   badge: "1",
  //   badgeColor: "bg-red-500",
  // },
  // {
  //   to: "/dashboard/rewards",
  //   icon: <Gift className="w-4 h-4" />,
  //   label: "Rewards",
  //   badge: "NEW",
  //   badgeColor: "bg-orange-500",
  // },
  // {
  //   to: "/dashboard/documents",
  //   icon: <FolderOpen className="w-4 h-4" />,
  //   label: "Documents",
  // },
  {
    to: "/dashboard/explore",
    icon: <Compass className="w-4 h-4" />,
    label: "Recommended Unis",
  },
];

const BOTTOM_NAV = [
  {
    to: "/dashboard",
    icon: <LayoutDashboard className="w-5 h-5" />,
    label: "Home",
    exact: true,
  },
  // {
  //   to: "/dashboard/applications",
  //   icon: <FileText className="w-5 h-5" />,
  //   label: "Apps",
  // },
  // {
  //   to: "/dashboard/chat",
  //   icon: <MessageCircle className="w-5 h-5" />,
  //   label: "Chat",
  // },
  // {
  //   to: "/dashboard/rewards",
  //   icon: <Gift className="w-5 h-5" />,
  //   label: "Rewards",
  // },
  {
    to: "/dashboard/profile",
    icon: <User className="w-5 h-5" />,
    label: "Profile",
  },
  {
    to: "/dashboard/course-finder",
    icon: <Sparkles className="w-5 h-5" />,
    label: "Finder",
  },
  {
    to: "/dashboard/career-test-result",
    icon: <BrainCircuit className="w-5 h-5" />,
    label: "Career",
  },
  {
    to: "/dashboard/exam-hub",
    icon: <GraduationCap className="w-5 h-5" />,
    label: "Exam",
  },
  {
    to: "/dashboard/explore",
    icon: <Compass className="w-5 h-5" />,
    label: "Unis",
  },
];

export default function DashboardLayout() {
  const { user, logout, isLoading, profileLoadError } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  if (isLoading) {
    return (
      <div
        className="min-h-screen bg-[#F5F5F8] flex items-center justify-center"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <div className="w-8 h-8 border-2 border-violet-200 border-t-violet-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (profileLoadError) {
    return (
      <div className="min-h-screen bg-[#F5F5F8] flex items-center justify-center p-6">
        <div className="w-full max-w-md rounded-2xl border border-red-100 bg-white p-6 text-center shadow-sm">
          <h1 className="text-lg font-semibold text-slate-900">
            Unable to load your profile
          </h1>
          <p className="mt-2 text-sm text-slate-600">{profileLoadError}</p>
          <div className="mt-5 flex justify-center gap-3">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="rounded-xl bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700"
            >
              Try again
            </button>
            <button
              type="button"
              onClick={() => void logout()}
              className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              Log out
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const tierColors: Record<string, string> = {
    Bronze: "text-amber-600 bg-amber-50 border-amber-200",
    Silver: "text-slate-500 bg-slate-50 border-slate-200",
    Gold: "text-yellow-600 bg-yellow-50 border-yellow-200",
    Platinum: "text-cyan-600 bg-cyan-50 border-cyan-200",
  };

  return (
    <div
      className="min-h-screen bg-[#F5F5F8] flex"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* ── Desktop Sidebar ── */}
      <aside className="hidden lg:flex flex-col w-60 xl:w-64 bg-white border-r border-slate-100 fixed inset-y-0 left-0 z-30">
        {/* Logo */}
        <div className="px-5 py-5 border-b border-slate-100">
          <NavLink to="/" className="flex items-center gap-2">
            <img src={logo} alt="" className="w-16 h-16" />
          </NavLink>
        </div>

        {/* User card */}
        <div className="px-4 py-4 border-b border-slate-50">
          <div className="flex items-center gap-3 p-3 rounded-2xl bg-slate-50/80">
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center text-white text-[12px] flex-shrink-0"
              style={{ backgroundColor: user.avatarColor, fontWeight: 700 }}
            >
              {user.name.charAt(0)}
            </div>
            <div className="min-w-0 flex-1">
              <p
                className="text-[13px] text-slate-900 truncate"
                style={{ fontWeight: 600 }}
              >
                {user.name}
              </p>
              {/* <span
                className={`inline-flex text-[10px] px-2 py-0.5 rounded-full border ${tierColors[user.tier]}`}
                style={{ fontWeight: 700 }}
              >
                {user.tier}
              </span> */}
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-3 overflow-y-auto">
          <p
            className="px-3 mb-2 text-[10px] text-slate-400 uppercase tracking-widest"
            style={{ fontWeight: 700 }}
          >
            Menu
          </p>
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.exact}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl mb-0.5 text-[13px] transition-all duration-150 ${
                  isActive
                    ? "bg-violet-50 text-violet-700"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span
                    className={isActive ? "text-violet-600" : "text-slate-400"}
                  >
                    {item.icon}
                  </span>
                  <span
                    className="flex items-center gap-1.5"
                    style={{ fontWeight: isActive ? 600 : 500 }}
                  >
                    {item.label}
                    {item.labelSuffix}
                  </span>
                  {item.badge && (
                    <span
                      className={`ml-auto text-[10px] px-2 py-0.5 rounded-full text-white ${item.badgeColor || "bg-slate-400"}`}
                      style={{ fontWeight: 700 }}
                    >
                      {item.badge}
                    </span>
                  )}
                </>
              )}
            </NavLink>
          ))}

          <div className="mt-4 pt-4 border-t border-slate-100">
            <p
              className="px-3 mb-2 text-[10px] text-slate-400 uppercase tracking-widest"
              style={{ fontWeight: 700 }}
            >
              Account
            </p>
            {/* <NavLink
              to="/dashboard/settings"
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl mb-0.5 text-[13px] text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-all"
            >
              <Settings className="w-4 h-4 text-slate-400" />
              <span style={{ fontWeight: 500 }}>Settings</span>
            </NavLink> */}
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] text-red-500 hover:bg-red-50 transition-all cursor-pointer"
            >
              <LogOut className="w-4 h-4" />
              <span style={{ fontWeight: 500 }}>Sign Out</span>
            </button>
          </div>
        </nav>

        {/* Points card at bottom */}
        <div className="p-4 border-t border-slate-100">
          <NavLink
            to="/course-finder"
            className="flex items-center gap-2 p-3 mb-3 rounded-2xl bg-violet-50 border border-violet-100 hover:bg-violet-100 transition-colors"
          >
            <Sparkles className="w-4 h-4 text-violet-600 flex-shrink-0" />
            <div className="min-w-0">
              <p
                className="text-[12px] text-violet-700 truncate"
                style={{ fontWeight: 600 }}
              >
                Course Finder
              </p>
              <p
                className="text-[10px] text-violet-500"
                style={{ fontWeight: 400 }}
              >
                Find your ideal course
              </p>
            </div>
          </NavLink>
          {/* <div className="p-3.5 rounded-2xl bg-gradient-to-r from-violet-600 to-purple-600 text-white">
            <p
              className="text-[10px] text-violet-200 uppercase tracking-widest mb-1"
              style={{ fontWeight: 700 }}
            >
              EduPoints Balance
            </p>
            <p className="text-[22px] text-white" style={{ fontWeight: 900 }}>
              {user.points.toLocaleString()}
            </p>
            <NavLink
              to="/dashboard/rewards"
              className="flex items-center gap-1 mt-1 text-[11px] text-violet-200 hover:text-white transition-colors"
              style={{ fontWeight: 600 }}
            >
              View rewards <ChevronRight className="w-3 h-3" />
            </NavLink>
          </div> */}
        </div>
      </aside>

      {/* ── Mobile Header ── */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b border-slate-100 px-4 h-14 flex items-center justify-between">
        <NavLink to="/" className="flex items-center gap-2">
          <img src={logo} alt="" className="w-10 h-10" />
        </NavLink>
        <div className="flex items-center gap-2">
          <button className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
            <Bell className="w-4 h-4 text-slate-600" />
          </button>
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center"
          >
            <Menu className="w-4 h-4 text-slate-600" />
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-slate-900/40 z-50 lg:hidden"
              onClick={() => setMobileMenuOpen(false)}
            />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25 }}
              className="fixed top-0 right-0 bottom-0 w-72 bg-white z-50 lg:hidden flex flex-col"
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
                <span
                  className="text-[14px] text-slate-900"
                  style={{ fontWeight: 700 }}
                >
                  Menu
                </span>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center"
                >
                  <X className="w-4 h-4 text-slate-600" />
                </button>
              </div>
              <nav className="flex-1 px-3 py-4 overflow-y-auto">
                {NAV_ITEMS.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.exact}
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-3 rounded-xl mb-1 text-[14px] transition-all ${
                        isActive
                          ? "bg-violet-50 text-violet-700"
                          : "text-slate-600 hover:bg-slate-50"
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <span
                          className={
                            isActive ? "text-violet-600" : "text-slate-400"
                          }
                        >
                          {item.icon}
                        </span>
                        <span
                          className="flex items-center gap-1.5"
                          style={{ fontWeight: isActive ? 600 : 500 }}
                        >
                          {item.label}
                          {item.labelSuffix}
                        </span>
                        {item.badge && (
                          <span
                            className={`ml-auto text-[10px] px-2 py-0.5 rounded-full text-white ${item.badgeColor || "bg-slate-400"}`}
                            style={{ fontWeight: 700 }}
                          >
                            {item.badge}
                          </span>
                        )}
                      </>
                    )}
                  </NavLink>
                ))}
              </nav>
              <div className="p-4 border-t border-slate-100">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-[14px] text-red-500 hover:bg-red-50 transition-all cursor-pointer"
                >
                  <LogOut className="w-4 h-4" />
                  <span style={{ fontWeight: 500 }}>Sign Out</span>
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* ── Main Content ── */}
      <main className="flex-1 lg:ml-60 xl:ml-64 pt-14 lg:pt-0 pb-20 lg:pb-0 min-w-0">
        <Outlet />
      </main>

      {/* ── Mobile Bottom Nav ── */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-slate-100 flex items-center justify-around px-2 h-16 safe-area-pb">
        {BOTTOM_NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.exact}
            className={({ isActive }) =>
              `flex flex-col items-center gap-1 px-3 py-1.5 rounded-xl transition-all ${isActive ? "text-violet-600" : "text-slate-400"}`
            }
          >
            {({ isActive }) => (
              <>
                <span
                  className={isActive ? "text-violet-600" : "text-slate-400"}
                >
                  {item.icon}
                </span>
                <span
                  className={`text-[10px] ${isActive ? "text-violet-600" : "text-slate-400"}`}
                  style={{ fontWeight: isActive ? 700 : 500 }}
                >
                  {item.label}
                </span>
              </>
            )}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
